import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../store.jsx";
import { useRole } from "../../hooks/useRole";
import { useSession } from "../../lib/auth-client";
import { usePerformanceReviews } from "../../hooks/usePerformanceReviews";
import {
  getReviewsMeta,
  listTeam,
  deleteReview,
  acknowledgeReview,
  updateReview,
} from "../../lib/api";
import { Icon, EmptyState, Pagination } from "../../components/Common.jsx";
import PageBoundary from "../../components/PageBoundary.jsx";
import StarRating from "../../components/reviews/StarRating.jsx";
import ReviewFormModal from "../../components/reviews/ReviewFormModal.jsx";

function Section({ title, text }) {
  if (!text) return null;
  return (
    <div style={{ paddingTop: 10 }}>
      <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 2 }}>{title}</div>
      <div style={{ fontSize: 13.5, whiteSpace: "pre-wrap" }}>{text}</div>
    </div>
  );
}

function ReviewsInner() {
  const { openModal, toast } = useApp();
  const role = useRole();
  const { data: session } = useSession();
  const myId = session?.user?.id;
  const canManage = role === "owner" || role === "manager";

  const [meta, setMeta] = useState({ ratingLabels: {}, periodTypes: [] });
  const [periodType, setPeriodType] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [team, setTeam] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getReviewsMeta()
      .then(setMeta)
      .catch(() => {});
  }, []);
  useEffect(() => {
    if (canManage) {
      listTeam()
        .then((d) => setTeam(d.users || []))
        .catch(() => {});
    }
  }, [canManage]);

  const filters = useMemo(() => {
    const f = { page, limit: pageSize, sort: "periodStart", dir: "desc" };
    if (periodType) f.periodType = periodType;
    if (employeeId) f.employeeId = employeeId;
    return f;
  }, [periodType, employeeId, page, pageSize]);

  const { reviews, total, loading, error, refresh } = usePerformanceReviews(filters);
  if (error) throw error;

  const openCreate = () => openModal(<ReviewFormModal meta={meta} onSaved={refresh} />, "lg");
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = (page - 1) * pageSize;
  const to = Math.min(total, from + reviews.length);
  const periodLabelOf = (r) => r.periodLabel || r.periodKey;

  const onDelete = async (r) => {
    if (!window.confirm("Delete this review?")) return;
    setBusy(true);
    try {
      await deleteReview(r.id);
      toast("Review deleted", "success");
      refresh();
    } catch (e) {
      toast(e.message || "Could not delete review", "error");
    } finally {
      setBusy(false);
    }
  };

  const onPublish = async (r) => {
    setBusy(true);
    try {
      await updateReview(r.id, { status: "published" });
      toast("Review published", "success");
      refresh();
    } catch (e) {
      toast(e.message || "Could not publish review", "error");
    } finally {
      setBusy(false);
    }
  };

  const onAck = async (r) => {
    setBusy(true);
    try {
      await acknowledgeReview(r.id);
      refresh();
    } catch (e) {
      toast(e.message || "Could not acknowledge review", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section>
      <div className="page-head">
        <div>
          <h1 className="page-title">Performance</h1>
          <p className="page-sub">
            {canManage ? "Reviews for your team" : "Your published performance reviews"}
          </p>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Icon name="plus" size={16} /> New Review
          </button>
        )}
      </div>

      <div className="table-toolbar">
        {canManage && (
          <div className="field" style={{ minWidth: 220, marginBottom: 0 }}>
            <select
              className="input"
              value={employeeId}
              onChange={(e) => {
                setEmployeeId(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All team members</option>
              {team.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            className={`filter-chip ${periodType === "" ? "active" : ""}`}
            onClick={() => {
              setPeriodType("");
              setPage(1);
            }}
          >
            All
          </button>
          {(meta.periodTypes || []).map((p) => (
            <button
              key={p.value}
              className={`filter-chip ${periodType === p.value ? "active" : ""}`}
              onClick={() => {
                setPeriodType(p.value);
                setPage(1);
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
          Loading reviews…
        </div>
      ) : reviews.length === 0 ? (
        <EmptyState
          title="No reviews yet"
          icon="award"
          desc={
            canManage
              ? "Create a performance review for a team member."
              : "You don't have any published reviews yet — your manager will publish them here."
          }
          cta={canManage ? "New Review" : undefined}
          onCta={canManage ? openCreate : undefined}
        />
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {reviews.map((r) => {
              const open = expanded === r.id;
              const isDraft = r.status === "draft";
              const mine = r.reviewer?.id && r.reviewer.id === myId;
              const canDelete = role === "owner" || (isDraft && mine);
              const canPublish = canManage && isDraft && (role === "owner" || mine);
              return (
                <div key={r.id} className="card" style={{ padding: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: 16,
                      cursor: "pointer",
                      flexWrap: "wrap",
                    }}
                    onClick={() => setExpanded(open ? null : r.id)}
                  >
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <div style={{ fontWeight: 600 }}>
                        {canManage ? r.employee?.name : periodLabelOf(r)}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                        {canManage ? periodLabelOf(r) : `Reviewed by ${r.reviewer?.name || "—"}`}
                      </div>
                    </div>
                    <StarRating value={r.overallRating} labels={meta.ratingLabels} size={16} />
                    <span style={{ fontSize: 12.5, color: "var(--text-muted)", minWidth: 110 }}>
                      {meta.ratingLabels?.[r.overallRating] || ""}
                    </span>
                    <span className={`badge ${r.status === "published" ? "badge-success" : "badge-muted"}`}>
                      {r.status}
                    </span>
                    {!canManage && r.status === "published" && !r.acknowledgedAt && (
                      <span className="badge badge-error">New</span>
                    )}
                    <Icon name={open ? "chevron-up" : "chevron-down"} size={16} />
                  </div>
                  {open && (
                    <div
                      style={{
                        padding: "0 16px 16px",
                        borderTop: "1px solid var(--border)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                      }}
                    >
                      <Section title="Strengths" text={r.strengths} />
                      <Section title="Areas to improve" text={r.improvements} />
                      <Section title="Feedback" text={r.feedback} />
                      {!r.strengths && !r.improvements && !r.feedback && (
                        <div style={{ paddingTop: 10, color: "var(--text-muted)", fontSize: 13 }}>
                          No written feedback — rating only.
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
                        {!canManage && r.status === "published" && !r.acknowledgedAt && (
                          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onAck(r)}>
                            <Icon name="check" size={14} /> Mark as read
                          </button>
                        )}
                        {canPublish && (
                          <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onPublish(r)}>
                            <Icon name="send" size={14} /> Publish
                          </button>
                        )}
                        {canDelete && (
                          <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => onDelete(r)}>
                            <Icon name="trash-2" size={14} /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {total > pageSize && (
            <Pagination
              page={page}
              pages={pages}
              total={total}
              from={from}
              to={to}
              pageSize={pageSize}
              onPage={setPage}
              onPageSize={(n) => {
                setPageSize(n);
                setPage(1);
              }}
            />
          )}
        </>
      )}
    </section>
  );
}

export default function PerformanceReviews() {
  return (
    <PageBoundary>
      <ReviewsInner />
    </PageBoundary>
  );
}
